import { useEffect, useRef, useState } from "react";
import { ArrowDownToLine, ArrowUpFromLine } from "lucide-react";
import { useFormContext, useWatch } from "react-hook-form";

import { OrderFormData } from "../OrderForm.schema";
import { BuyOrderRow, BuyOrders } from "./BuyOrders";
import { SellOrderRow, SellOrders } from "./SellOrders";
import { QuerySecurity } from "@/api/queries/types";
import { Portfolio } from "@/api/types";
import { cn } from "@/lib/utils";

function fmt(n: number) {
  return new Intl.NumberFormat("no-NO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

function OrderSummary({
  buyOrders,
  sellOrders,
  amountToInvest,
  modelType,
}: {
  buyOrders: BuyOrderRow[];
  sellOrders: SellOrderRow[];
  amountToInvest: number;
  modelType: string;
}) {
  const isNoModel = modelType === "NOMODEL";
  const totalBuyAmt = buyOrders
    .filter((r) => r.instrumentName)
    .reduce((sum, r) => sum + (parseFloat(r.amountInput) || 0), 0);

  const remaining = isNoModel ? amountToInvest - totalBuyAmt : null;

  const totalSellAmt = sellOrders
    .filter((r) => r.name)
    .reduce(
      (sum, r) => sum + (parseFloat(r.sellQty) || 0) * (r.marketUnitPrice || 0),
      0
    );

  const net = totalBuyAmt - totalSellAmt;

  // Securities that appear in both a buy order and a sell order with qty > 0
  const buySecurityIds = new Set(
    buyOrders
      .filter((r) => r.securityId && r.instrumentName)
      .map((r) => r.securityId)
  );
  const conflicts = sellOrders.filter(
    (r) => parseFloat(r.sellQty) > 0 && buySecurityIds.has(r.securityId)
  );

  return (
    <div className="space-y-3">
      {conflicts.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-600 dark:bg-amber-950/30">
          <p className="mb-1 text-xs font-semibold text-amber-700 dark:text-amber-400">
            Kjøp og salg på samme instrument
          </p>
          {conflicts.map((r) => (
            <p
              key={r.securityId}
              className="text-xs text-amber-600 dark:text-amber-300"
            >
              {r.name}
            </p>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 border-t border-border/50 pt-3 sm:grid-cols-4">
        {[
          {
            label: "Antall instrumenter",
            value: buyOrders.filter((r) => r.instrumentName).length,
          },
          { label: "Total kjøp", value: `NOK ${fmt(totalBuyAmt)}` },
          { label: "Total salg", value: `NOK ${fmt(totalSellAmt)}` },
          {
            label: isNoModel ? "Gjenstående" : "Netto",
            value:
              isNoModel && amountToInvest > 0
                ? `NOK ${fmt(remaining ?? 0)}`
                : `NOK ${fmt(net)}`,
            tone:
              isNoModel && amountToInvest > 0
                ? Math.abs(remaining ?? 0) < 0.01
                  ? "text-emerald-600"
                  : "text-rose-600"
                : "text-foreground",
          },
        ].map(({ label, value, tone }) => (
          <div key={label} className="rounded-lg bg-muted/30 px-3 py-2">
            <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
              {label}
            </p>
            <p className={cn("mt-1 text-sm font-bold tabular-nums", tone)}>
              {value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

type Props = {
  portfolios: Portfolio[];
  securities: QuerySecurity[];
};

export function OrdreModulSection({ portfolios, securities }: Props) {
  const {
    register,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<OrderFormData>();

  // Capture initial (saved) rows once at mount — used when editing an existing order
  const initialBuyRowsRef = useRef<BuyOrderRow[]>(
    (getValues("buyOrders") as BuyOrderRow[]) ?? []
  );
  const initialSellRowsRef = useRef<SellOrderRow[]>(
    (getValues("sellOrders") as SellOrderRow[]) ?? []
  );
  const modelType = useWatch({ name: "dpModelType" }) as string;
  const portfolioId = useWatch({ name: "dpPortfolioId" });
  const amountToInvest =
    (useWatch({ name: "dpAmountToInvest" }) as number) ?? 0;
  const buyOrders = (useWatch({ name: "buyOrders" }) ?? []) as BuyOrderRow[];
  const sellOrders = (useWatch({ name: "sellOrders" }) ?? []) as SellOrderRow[];
  const [activeTab, setActiveTab] = useState<"buy" | "sell">("buy");

  const isNoModel = modelType === "NOMODEL";
  const showOrderModule = isNoModel;
  const selectedPortfolio = portfolios.find(
    (p) => String(p.id) === String(portfolioId)
  );

  // Keep ordreModul synced for backwards compatibility with existing payloads.
  useEffect(() => {
    setValue("ordreModul", isNoModel ? "yes" : "no");
  }, [isNoModel, setValue]);

  // Clear order arrays when leaving NOMODEL.
  useEffect(() => {
    if (!showOrderModule) {
      setValue("buyOrders", []);
      setValue("sellOrders", []);
    }
  }, [showOrderModule, setValue]);

  return (
    <div className="space-y-4">
      {!isNoModel && (
        <div className="rounded-lg border border-dashed border-border px-3 py-3 text-xs text-muted-foreground">
          Velg <span className="font-medium">No Model</span> under Model for a
          legge inn kjøp/salg manuelt.
        </div>
      )}

      {/* Buy/Sell order grids — always shown for NOMODEL */}
      {showOrderModule && (
        <>
          <div className="overflow-hidden rounded-xl border border-border/70 bg-card shadow-[0_1px_3px_rgba(15,23,42,0.04)]">
            <div className="flex items-center justify-between border-b border-border/60 px-3 py-2">
              <div className="flex items-center gap-1 rounded-lg bg-muted/50 p-1">
                {[
                  {
                    key: "buy" as const,
                    label: "Kjøpsordre",
                    icon: ArrowDownToLine,
                  },
                  {
                    key: "sell" as const,
                    label: "Salgsordre",
                    icon: ArrowUpFromLine,
                  },
                ].map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActiveTab(key)}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-semibold transition-colors",
                      activeTab === key
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <Icon className="size-3.5" />
                    {label}
                  </button>
                ))}
              </div>
              <span className="hidden text-[10px] text-muted-foreground sm:inline">
                {activeTab === "buy"
                  ? "Legg til instrumenter du vil kjøpe"
                  : "Velg beholdning du vil selge"}
              </span>
            </div>

            <div className="p-3">
              {activeTab === "buy" ? (
                <BuyOrders
                  selectedPortfolio={selectedPortfolio}
                  securities={securities}
                  initialRows={initialBuyRowsRef.current}
                  onChange={(rows) => setValue("buyOrders", rows)}
                />
              ) : (
                <SellOrders
                  selectedPortfolio={selectedPortfolio}
                  savedRows={initialSellRowsRef.current}
                  onChange={(rows) => setValue("sellOrders", rows)}
                />
              )}
            </div>

            <div className="border-t border-border/60 bg-muted/15 px-3 py-3">
              <OrderSummary
                buyOrders={buyOrders}
                sellOrders={sellOrders}
                amountToInvest={amountToInvest}
                modelType={modelType}
              />
            </div>
          </div>
          {errors.buyOrders?.message && (
            <p className="mt-2 text-xs text-red-500 dark:text-red-400">
              {errors.buyOrders.message as string}
            </p>
          )}
        </>
      )}

      {/* Hidden field registration kept so schema/payload remains stable */}
      <input type="hidden" {...register("ordreModul")} />
    </div>
  );
}
